import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';
import { MapPin, Navigation, Phone, Mail, Clock } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import 'leaflet/dist/leaflet.css';

// Custom marker icon
const customMarkerIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const FreeMap: React.FC = () => {
  const { t } = useLanguage();
  
  // Coordonnées pour Schifflange, Luxembourg
  const position: [number, number] = [49.5094, 6.0158]; // Approximation pour Schifflange
  const mapCenter: [number, number] = [49.5094, 6.0158];

  useEffect(() => {
    // Fix pour les icônes Leaflet par défaut
    delete (Icon.Default.prototype as any)._getIconUrl;
    Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }, []);

  const handleGetDirections = () => {
    const address = encodeURIComponent(`${t('common.company.address')}, ${t('common.company.postalCode')} ${t('common.company.city')}, ${t('common.company.country')}`);
    window.open(`https://www.openstreetmap.org/directions?to=${position[0]},${position[1]}`, '_blank');
  };

  const handleOpenInMaps = () => {
    const address = encodeURIComponent(`${t('common.company.address')}, ${t('common.company.postalCode')} ${t('common.company.city')}, ${t('common.company.country')}`);
    // Ouvrir dans l'app de cartes par défaut du système
    window.open(`https://maps.apple.com/?q=${address}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.4 }}
      className="bg-white rounded-2xl shadow-2xl overflow-hidden"
    >
      <div className="p-8 border-b">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {t('contact.location.title')}
        </h3>
        <p className="text-gray-600">
          {t('contact.location.description')}
        </p>
      </div>

      {/* Map Container */}
      <div className="relative h-96">
        <MapContainer
          center={mapCenter}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          className="z-10"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <Marker position={position} icon={customMarkerIcon}>
            <Popup className="custom-popup">
              <div className="p-4 min-w-[280px]">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">
                      {t('common.company.name')}
                    </h4>
                    <p className="text-red-600 font-medium">
                      Services Premium Automobiles
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">
                        {t('common.company.address')}
                      </div>
                      <div className="text-gray-600">
                        {t('common.company.postalCode')} {t('common.company.city')}
                      </div>
                      <div className="text-gray-600">
                        {t('common.company.country')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-600" />
                    <a
                      href={`tel:${t('common.company.phone')}`}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      {t('common.company.phone')}
                    </a>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <a
                      href={`mailto:${t('common.company.email')}`}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      {t('common.company.email')}
                    </a>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-gray-600 mt-1 flex-shrink-0" />
                    <div className="text-sm">
                      <div className="text-gray-900 font-medium">
                        {t('common.openingHours.title')}
                      </div>
                      <div className="text-gray-600 space-y-1">
                        <div>{t('common.openingHours.weekdays')}</div>
                        <div>{t('common.openingHours.saturday')}</div>
                        <div className="text-gray-500">{t('common.openingHours.sunday')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        </MapContainer>

        {/* Overlay Controls */}
        <div className="absolute top-4 right-4 z-20 flex flex-col space-y-2">
          <motion.button
            onClick={handleGetDirections}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-sm font-medium text-gray-700 hover:text-red-600"
          >
            <Navigation className="w-4 h-4" />
            <span>Itinéraire</span>
          </motion.button>

          <motion.button
            onClick={handleOpenInMaps}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg shadow-lg hover:shadow-xl hover:bg-red-700 transition-all duration-200 text-sm font-medium"
          >
            <MapPin className="w-4 h-4" />
            <span>Ouvrir</span>
          </motion.button>
        </div>

        {/* Map Info Overlay */}
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900">
                    {t('common.company.name')}
                  </h4>
                  <p className="text-sm text-gray-600">
                    Schifflange, Luxembourg
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  Parking disponible
                </p>
                <p className="text-xs text-gray-500">
                  Accès facile
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default FreeMap;